import { memo, useMemo } from 'react';
import { useGetPopularEventsQuery } from '@/store/api/sportsApi';
import { useNavigate } from 'react-router-dom';
import { sportsIconMap } from '@/utils/constants';
import { useBrandFeatures } from '@/brands';

const HeaderTopGames = () => {
  const navigate = useNavigate();
  // Every card here opens `/game-details/...`, which `FeatureGate` 404s once the
  // Sports grant is gone, so the rail goes with it rather than leading nowhere.
  const { sports: sportsOn } = useBrandFeatures();
  const { data: popularEventsData } = useGetPopularEventsQuery(undefined, {
    skip: !sportsOn,
  });

  const events = useMemo(() => {
    // only use real popular events from the API
    return (popularEventsData?.events ?? []).slice(0, 5); // show up to 5 cards
  }, [popularEventsData]);

  if (!sportsOn || !events.length) return null;

  return (
    <div className="xl:hidden latest-events">
      <style>{`
        .latest-events {
          background: #0088cc;   /* blue shows in the 4px gaps (top + between cards) */
        }
        /* Native horizontal scroll — no JS re-measure, so the icon/text never flash/zoom */
        .latest-events-track {
          display: flex;
          align-items: stretch;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;   /* momentum scroll on iOS */
          scrollbar-width: none;                /* hide scrollbar (Firefox) */
        }
        .latest-events-track::-webkit-scrollbar { display: none; }  /* hide scrollbar (Chrome/Safari) */

        .latest-event-item {
          box-sizing: border-box;
          flex-shrink: 0;            /* keep fixed width so the row overflows and scrolls */
          display: flex;
          align-items: center;       /* vertical center */
          gap: 5px;                                    /* ~5px between icon and text */
          width: 207px;
          height: 29px;
          background: #2C3E50;
          padding: 4px;                                /* inner padding 4px */
          margin: 4px 4px 0 0;                         /* 4px top + 4px gap between cards (blue shows) */
          border-bottom: 2px solid #0088cc;            /* cyan/blue separator */
          cursor: pointer;
          transition: background .15s ease;
        }
        .latest-event-item:active { background: #243140; }

        .lei-content {
          display: flex;
          align-items: center;       /* icon + text vertically centered */
          gap: 5px;                  /* ~5px between icon and text */
          width: 200px;
          height: 21px;
          overflow: hidden;
        }
        .lei-icon {
          height: 16px;
          width: 16px;
          object-fit: contain;
          flex-shrink: 0;
          margin-left: 6px;                 /* nudge the icon slightly to the right */
          filter: brightness(0) invert(1) opacity(0.8);
          /* same smooth live fade as the title */
          animation: leiNameFade 1.8s ease-in-out infinite;
          will-change: opacity;
        }
        .lei-name {
          flex: 1;
          min-width: 0;
          margin: 0;
          font-family: 'Roboto Condensed', sans-serif;
          font-size: 14px;
          font-weight: 600;
          line-height: 1;
          color: #FFFFFF;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-text-size-adjust: 100%;   /* stop mobile browsers auto-resizing ("zoom") the text */
          text-size-adjust: 100%;
          /* Smooth live fade: full → dim → full, looping. Only opacity animates,
             so text position, box size and layout never change. */
          animation: leiNameFade 1.8s ease-in-out infinite;
          will-change: opacity;
        }
        @keyframes leiNameFade {
          0%   { opacity: 1; }
          50%  { opacity: 0.35; }
          100% { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .lei-name, .lei-icon { animation: none; }
        }
      `}</style>

      <div className="latest-events-track">
        {events.map((event) => (
          <button
            key={event.id}
            onClick={() => navigate(`/game-details/${event.sport}/${event.id}`)}
            className="latest-event-item"
          >
            <span className="lei-content">
              <img
                className="lei-icon"
                src={sportsIconMap[event.sport] ?? ''}
                alt={event.sport}
              />
              <span className="lei-name">{event.name}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default memo(HeaderTopGames);
